import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useEffect, useRef, useState, useCallback } from "react";
import {
  GeoJSON,
  LayersControl,
  MapContainer,
  TileLayer,
  useMap,
} from "react-leaflet";

function useGeoJSON(url, layerName) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(url)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Erro HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((geojson) => {
        setData(geojson);
      })
      .catch((err) => {
        console.error(`Erro ao carregar ${layerName}:`, err.message);
      });
  }, [url, layerName]);

  return data;
}

function MapRefCatcher({ onReady, onMapClick }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    // Guarda contra _onZoomTransitionEnd disparar após unmount
    const original = map._onZoomTransitionEnd?.bind(map);
    if (original) {
      map._onZoomTransitionEnd = function (...args) {
        try { original(...args); } catch {}
      };
    }

    onReady?.(map);
    const handler = () => onMapClick?.();
    map.on("click", handler);
    return () => {
      map.off("click", handler);
      try { map.stop(); } catch {}
    };
  }, [map, onReady, onMapClick]);

  return null;
}

const SETOR_COLORS = { 1: "#FFAD59", 2: "#b2df8a", 3: "#a6cee3", 4: "#fb9a99" };

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function lerpColor(a, b, t) {
  const [r1, g1, b1] = hexToRgb(a);
  const [r2, g2, b2] = hexToRgb(b);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const bl = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r},${g},${bl})`;
}

function LeafletMap({
  setor1,
  setor2,
  setor3,
  setor4,
  setSelectedId,
  highlightedId,
  onDeselect,
}) {
  const mapRef = useRef(null);

  const capelaData = useGeoJSON("/capela_wgs84.geojson", "capela");
  const escritorioData = useGeoJSON("/escritorio_wgs84.geojson", "escritório");
  const acessosData = useGeoJSON("/acessos_wgs84.geojson", "acessos");
  const murosInternosData = useGeoJSON(
    "/muros_internos_wgs84.geojson",
    "muros internos",
  );
  const perimetroCemiterioData = useGeoJSON(
    "/perimetro_cemiterio_wgs84.geojson",
    "perímetro",
  );
  const velorioData = useGeoJSON("/velorio_wgs84.geojson", "velório");

  /* estado de seleção */
  const [selectedId, setSelectedId2] = useState(null);

  // camada de pulso separada
  const [pulseFeature, setPulseFeature] = useState(null);
  const [pulseBaseColor, setPulseBaseColor] = useState(null);
  const [pulsePhase, setPulsePhase] = useState(0);
  const rafRef = useRef(null);
  const layersMapRef = useRef({}); // id → { layer, baseColor, feature }

  const stopPulse = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setPulseFeature(null);
    setPulseBaseColor(null);
    setPulsePhase(0);
  }, []);

  const startPulse = useCallback((id) => {
    const entry = layersMapRef.current[id];
    if (!entry) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setPulseFeature(entry.feature);
    setPulseBaseColor(entry.baseColor);
    const t0 = performance.now();
    const animate = (now) => {
      const phase = (Math.sin(((now - t0) / 1000) * Math.PI * 2 * 0.7) + 1) / 2;
      setPulsePhase(phase);
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
  }, []);

  const onEachFeature = (feature, layer) => {
    const baseColor = SETOR_COLORS[feature.properties.quadra] || "#888888";
    layersMapRef.current[feature.properties.id] = { layer, baseColor, feature };

    layer.on({
      click: (e) => {
        L.DomEvent.stopPropagation(e);
        setSelectedId2(feature.properties.id);
        startPulse(feature.properties.id);
      },
    });

    layer.bindPopup(() => {
      const div = document.createElement("div");

      div.innerHTML = `
        <div style="font-size:13px; margin-bottom:6px;">
          <b>Número:</b> ${feature.properties.numero}<br/>
          <b>Setor:</b> ${feature.properties.quadra}
        </div>
      `;

      const button = document.createElement("button");
      button.innerText = "Ver falecidos";

      // 🎨 ESTILO inspirado no seu sistema
      button.style.backgroundColor = "#E29563"; // laranja do topo
      button.style.color = "#fff";
      button.style.border = "none";
      button.style.padding = "6px 10px";
      button.style.borderRadius = "6px";
      button.style.cursor = "pointer";
      button.style.fontSize = "12px";
      button.style.width = "100%";
      button.style.transition = "0.2s";

      // efeito hover
      button.onmouseenter = () => {
        button.style.backgroundColor = "#cf7f4e";
      };
      button.onmouseleave = () => {
        button.style.backgroundColor = "#E29563";
      };

      button.onclick = () => {
        setSelectedId(feature.properties.id);
      };

      div.appendChild(button);

      return div;
    });
  };

  const onEachFeatureCapela = (feature, layer) => {
    layer.bindPopup("<b>Capela Mortuária</b>");

    layer.on({
      click: () => {
        layer.openPopup();
      },
    });
  };

  const onEachFeatureVelorio = (feature, layer) => {
    layer.bindPopup(() => {
      const div = document.createElement("div");

      div.innerHTML = `
        <div style="font-size:13px; margin-bottom:6px;">
          <b>Velório</b>
        </div>
      `;

      return div;
    });
  };

  const onEachFeatureOffice = (feature, layer) => {
    layer.bindPopup(() => {
      const div = document.createElement("div");

      div.innerHTML = `
        <div style="font-size:13px; margin-bottom:6px;">
          <b>Escritório administrativo do cemitério</b>
        </div>
      `;

      return div;
    });
  };

  /* 🔥 NOVO: estilo dinâmico */
  const getStyle = (feature, color) => {
    const isSelected = feature.properties.id === selectedId;

    return {
      fillColor: isSelected ? "#ffffff" : color,
      fillOpacity:1.0,
      color: isSelected ? "#000" : "#000",
      weight: isSelected ? 2 : 0.5,
    };
  };

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const lastHighlightedId = useRef(null);

  useEffect(() => {
    if (!highlightedId) {
      stopPulse();
      return;
    }
    if (lastHighlightedId.current === highlightedId) return;
    lastHighlightedId.current = highlightedId;

    const entry = layersMapRef.current[highlightedId];
    if (entry && mapRef.current) {
      try {
        entry.layer.bringToFront();
        const center = entry.layer.getBounds().getCenter();
        mapRef.current.flyTo(center, 22, { animate: true, duration: 1.2 });
      } catch {}
    }

    startPulse(highlightedId);
  }, [highlightedId, startPulse, stopPulse]);
  return (
    <div
      style={{ width: "100%", height: "100%", position: "relative", zIndex: 0 }}
    >
      <MapContainer
        center={[-19.8239, -43.3598]}
        zoom={19}
        minZoom={19}
        maxZoom={23}
        preferCanvas={true}
        style={{
          width: "100%",
          height: "100%",
          zIndex: 0,
          position: "relative",
        }}
      >
        <LayersControl position="topright">
          {/* BASE LAYERS (fundo) */}

          <LayersControl.BaseLayer checked name="OpenStreetMap">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={25}
            />
          </LayersControl.BaseLayer>

          {/* BASE LAYERS (fundo) */}
          <LayersControl.Overlay checked name="Imagem do cemitério">
            <TileLayer
              url="https://tiles.cemiteriosaogoncalo.com.br/{z}/{x}/{y}.webp"
              minZoom={19}
              maxZoom={23}
              maxNativeZoom={23}
              opacity={1.0}
            />
          </LayersControl.Overlay>

          {/* OVERLAYS */}
          {perimetroCemiterioData && (
            <LayersControl.Overlay
              checked
              name='<span style="display:inline-block;width:16px;height:3px;background:#ffffff;margin-right:6px;"></span>Perímetro do Cemitério'
            >
              <GeoJSON
                data={perimetroCemiterioData}
                style={{
                  color: "#ffffff",
                  weight: 3,
                  fillOpacity: 0.2,
                }}
              />
            </LayersControl.Overlay>
          )}

          {acessosData && (
            <LayersControl.Overlay
              checked
              name='<span style="display:inline-block;width:16px;height:3px;background:#716761;margin-right:6px;"></span>Acessos'
            >
              <GeoJSON
                data={acessosData}
                style={{
                  color: "#716761",
                  weight: 2,
                }}
              />
            </LayersControl.Overlay>
          )}

          {murosInternosData && (
            <LayersControl.Overlay
              checked
              name='<span style="display:inline-block;width:16px;height:3px;background:#000000;margin-right:6px;"></span>Muros Internos'
            >
              <GeoJSON
                data={murosInternosData}
                style={{
                  color: "#000000",
                  weight: 2,
                }}
              />
            </LayersControl.Overlay>
          )}

          {capelaData && (
            <LayersControl.Overlay
              checked
              name='<span style="display:inline-block;width:12px;height:12px;background:#018d81;margin-right:6px;"></span>Capela'
            >
              <GeoJSON
                data={capelaData}
                style={(f) => getStyle(f, "#018d81")}
                onEachFeature={onEachFeatureCapela}
              />
            </LayersControl.Overlay>
          )}

          {escritorioData && (
            <LayersControl.Overlay
              checked
              name='<span style="display:inline-block;width:12px;height:12px;background:#07ff5e;margin-right:6px;"></span>Escritório'
            >
              <GeoJSON
                data={escritorioData}
                style={(f) => getStyle(f, "#07ff5e")}
                onEachFeature={onEachFeatureOffice}
              />
            </LayersControl.Overlay>
          )}

          {velorioData && (
            <LayersControl.Overlay
              checked
              name='<span style="display:inline-block;width:12px;height:12px;background:#9d00ff;margin-right:6px;"></span>Velório'
            >
              <GeoJSON
                data={velorioData}
                style={(f) => getStyle(f, "#9d00ff")}
                onEachFeature={onEachFeatureVelorio}
              />
            </LayersControl.Overlay>
          )}

          {/* SETORES */}
          {setor1 && (
            <LayersControl.Overlay
              checked
              name='<span style="display:inline-block;width:12px;height:12px;background:#FFAD59;margin-right:6px;"></span>Setor 1'
            >
              <GeoJSON
                data={setor1}
                style={() => ({ color: "#000000", weight: 1, fillColor: "#FFAD59", fillOpacity: 1.0 })}
                onEachFeature={onEachFeature}
              />
            </LayersControl.Overlay>
          )}

          {setor2 && (
            <LayersControl.Overlay
              checked
              name='<span style="display:inline-block;width:12px;height:12px;background:#b2df8a;margin-right:6px;"></span>Setor 2'
            >
              <GeoJSON
                data={setor2}
                style={() => ({ color: "#000000", weight: 1, fillColor: "#b2df8a", fillOpacity: 1.0 })}
                onEachFeature={onEachFeature}
              />
            </LayersControl.Overlay>
          )}

          {setor3 && (
            <LayersControl.Overlay
              checked
              name='<span style="display:inline-block;width:12px;height:12px;background:#a6cee3;margin-right:6px;"></span>Setor 3'
            >
              <GeoJSON
                data={setor3}
                style={() => ({ color: "#000000", weight: 1, fillColor: "#a6cee3", fillOpacity: 1.0 })}
                onEachFeature={onEachFeature}
              />
            </LayersControl.Overlay>
          )}

          {setor4 && (
            <LayersControl.Overlay
              checked
              name='<span style="display:inline-block;width:12px;height:12px;background:#fb9a99;margin-right:6px;"></span>Setor 4'
            >
              <GeoJSON
                data={setor4}
                style={() => ({ color: "#000000", weight: 1, fillColor: "#fb9a99", fillOpacity: 1.0 })}
                onEachFeature={onEachFeature}
              />
            </LayersControl.Overlay>
          )}
        </LayersControl>

        {/* camada de pulso: renderiza apenas o jazigo selecionado com animação suave */}
        {pulseFeature && (
          <GeoJSON
            key={pulseFeature.properties.id}
            data={pulseFeature}
            style={() => ({
              fillColor: lerpColor(pulseBaseColor, "#ffff00", pulsePhase),
              fillOpacity: 0.7 + 0.3 * pulsePhase,
              color: lerpColor("#000000", "#cccc00", pulsePhase),
              weight: 1 + 3 * pulsePhase,
            })}
          />
        )}

        <MapRefCatcher
          onReady={(mapInstance) => {
            mapRef.current = mapInstance;
          }}
          onMapClick={() => {
            stopPulse();
            lastHighlightedId.current = null;
            onDeselect?.();
          }}
        />
      </MapContainer>
    </div>
  );
}

export default LeafletMap;
